import { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import api from "../../chat/api";
import { PERIODS } from "./dashboardFormat";

const orgId = () => Cookies.get("selected_org_id") || "";
const keyFor = (period) => `${orgId()}:${period}`;

// Responses are kept per workspace and period, so a period is never shown with another period's numbers.
export default function useDashboardData(period) {
  const [byKey, setByKey] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);
  const [error, setError] = useState(null);
  const inFlight = useRef(new Set());

  const fetchPeriod = useCallback(async (p, { quiet = false } = {}) => {
    const key = keyFor(p);
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    if (!quiet) {
      setLoadingKey(key);
      setError(null);
    }
    try {
      const params = new URLSearchParams({ period: p });
      if (orgId()) params.set("organization_id", orgId());
      const res = await api.get(`/api/v1/rfp/dashboard/?${params.toString()}`);
      setByKey((prev) => ({ ...prev, [key]: res.data }));
    } catch (err) {
      if (!quiet && key === keyFor(p)) setError(err);
    } finally {
      inFlight.current.delete(key);
      if (!quiet) setLoadingKey((current) => (current === key ? null : current));
    }
  }, []);

  const data = byKey[keyFor(period)] || null;

  useEffect(() => {
    if (!data) fetchPeriod(period);
  }, [period, data, fetchPeriod]);

  // Once the visible period has loaded, fetch the others quietly so switching is instant.
  useEffect(() => {
    if (!data) return;
    PERIODS.forEach(({ key }) => {
      if (key !== period && !byKey[keyFor(key)]) fetchPeriod(key, { quiet: true });
    });
  }, [data, period, byKey, fetchPeriod]);

  const reload = useCallback(() => {
    setByKey({});
    fetchPeriod(period);
  }, [period, fetchPeriod]);

  useEffect(() => {
    window.addEventListener("workspace-changed", reload);
    return () => window.removeEventListener("workspace-changed", reload);
  }, [reload]);

  return { data, loading: loadingKey === keyFor(period), error: data ? null : error, reload };
}
